import PropTypes from 'prop-types'
import { Button, Popconfirm } from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  MessageOutlined,
  ShopOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { toPath } from '../../routes'
import CircleCheckIcon from '../Common/CircleCheckIcon'

const useStyles = createUseStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    minWidth: 0,
    overflowWrap: 'anywhere',
  },
  line: {
    fontSize: 13,
    color: '#595959',
    overflowWrap: 'anywhere',
  },
  lineIcon: {
    marginRight: 6,
    color: '#6b6b6b',
  },
  // The three row actions share the last line and wrap when they don't fit.
  actionsLine: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
    marginTop: 4,
  },
})

const ACTION_BUTTON_STYLE = { paddingLeft: 0 }

// Every desktop column fits on the card, so these rows need no expand toggle.
export default function UserMobileCard({
  user,
  showPractice = false,
  showActive = false,
  onDelete,
  onPasswordReset,
}) {
  const classes = useStyles()
  const { active, email, id, positionLabel, practice, role } = user
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ')
  // Role and position are both optional, so drop the separator when one is missing.
  const roleLine = [role?.name, positionLabel].filter(Boolean).join(' · ')

  return (
    <div className={classes.card}>
      <div className={classes.name}>{name || '—'}</div>
      <div className={classes.line}>{email || '—'}</div>
      {roleLine && <div className={classes.line}>{roleLine}</div>}
      {showPractice && practice?.name && (
        <div className={classes.line}>
          <ShopOutlined className={classes.lineIcon} />
          {practice.name}
        </div>
      )}
      {showActive && (
        <div className={classes.line}>
          <CircleCheckIcon checked={active} /> {active ? 'Active' : 'Inactive'}
        </div>
      )}
      <div className={classes.actionsLine}>
        <Popconfirm
          title={`Do you want to send the password reset email to ${email}?`}
          onConfirm={() => onPasswordReset(email)}
          okText="Yes"
          cancelText="No"
          placement="topLeft"
        >
          <Button
            type="link"
            size="small"
            icon={<MessageOutlined />}
            style={ACTION_BUTTON_STYLE}
          >
            Reset password
          </Button>
        </Popconfirm>
        <Link to={toPath.users.user.edit({ id })} className="edit-button">
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            style={ACTION_BUTTON_STYLE}
          >
            Edit
          </Button>
        </Link>
        <Popconfirm
          title="Are you sure you want to deactivate this user?"
          onConfirm={() => onDelete(id)}
          okText="Yes"
          cancelText="No"
          placement="topLeft"
        >
          <Button
            type="link"
            size="small"
            icon={<DeleteOutlined />}
            className="delete-button"
            style={ACTION_BUTTON_STYLE}
          >
            Deactivate
          </Button>
        </Popconfirm>
      </div>
    </div>
  )
}

UserMobileCard.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    active: PropTypes.bool,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    positionLabel: PropTypes.string,
    practice: PropTypes.shape({
      name: PropTypes.string,
    }),
    role: PropTypes.shape({
      name: PropTypes.string,
    }),
  }).isRequired,
  showPractice: PropTypes.bool,
  showActive: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
  onPasswordReset: PropTypes.func.isRequired,
}
